import cn from 'classnames';
import { forwardRef } from 'react';

import { Tooltip } from '../../popovers';

import type { ForwardedRef, ReactElement } from 'react';

import type { FieldLabelProps } from './field-label.types';

import './field-label.scss';

/**
 * @internal
 * The `FieldLabel` component renders a label for a form field like `input`, `select`, `textarea`, etc.
 * It has a default bold view and can be changed to a normal view for checkbox, radio or toggle fields.
 * Internally used with the `BaseField` component to render a label for a field.
 * Might be used as a standalone component for custom form fields, check the usage example.
 *
 * ### Usage
 *
 * ```tsx
 * import { FieldLabel } from '@bloomreach/react-banana-ui';
 * import { FieldInput } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomLabel() {
 *   return (
 *     <>
 *      <FieldInput id="input" />
 *      <FieldLabel htmlFor="input">Write an example of component usage</FieldLabel>
 *    </>
 *   );
 * }
 * ```
 */
const FieldLabel = forwardRef(
  (props: FieldLabelProps, forwardedRef: ForwardedRef<HTMLLabelElement> | null): ReactElement => {
    const {
      children,
      className,
      htmlFor,
      required,
      tooltip,
      view = 'bold',
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const classes = cn(className, 'rbui-field-label', {
      'rbui-field-label--bold': view === 'bold',
      'rbui-field-label--normal': view === 'normal',
      'rbui-field-label--required': required,
    });

    const LabelContent = (
      <label className={classes} htmlFor={htmlFor} ref={forwardedRef} {...rest}>
        {children}
      </label>
    );

    if (tooltip) {
      return (
        <Tooltip renderOnIcon title={tooltip}>
          {LabelContent}
        </Tooltip>
      );
    }

    return LabelContent;
  },
);

FieldLabel.displayName = 'FieldLabel';

export default FieldLabel;
